import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Collapse,
  Divider,
  Group,
  Radio,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { ContentCard } from '../../../components/common/ContentCard';
import { ScaleQuestion } from '../../../components/session/ScaleQuestion';
import { POST_SESSION_PRIMARY_BG } from '../../../components/session/postSessionCard';
import { CheckCircle, ChevronDown, ChevronUp, FileText } from 'react-feather';
import {
  getMissingRequiredFeedbackKeys,
  peerLeaderFeedbackSections,
  type PeerLeaderFeedbackQuestion,
} from '../../../config/peerLeaderFeedbackQuestionSets';

/**
 * MED-817 / E4.5 — Peer Leader "Medley Feedback Form" card.
 *
 * AC9: renders the two-section question set (As Session Leader / As a
 * Participant) with labeled 5-point scales, single-selects, and open text.
 * AC10: Save Draft + Submit Feedback. AC12: the section collapses on Submit to
 * a "Submitted" summary with an expand chevron; once submitted the inputs are
 * read-only (the leader can expand to review but not edit).
 *
 * MED-1056 polish (visual-consistency pass driven off MED-1054/1055):
 *   - Shared post-session card look (ContentCard) + the sibling header
 *     treatment (small inline icon + 15/700 title).
 *   - 5-point scales reuse the shared `ScaleQuestion` (labeled-button rows,
 *     medleyGreen selected) so the "As a Participant" set renders identically to
 *     the participant card (MED-1055). Categorical single-selects stay as radios.
 *   - Open-text textareas + footer match the siblings; "Submitted" drops the
 *     timestamp; Submit uses the shared navy primary.
 * Structure/content/behavior unchanged (two sections, collapse-on-submit,
 * validation, post-submit read-only). All manual entry — no AI pre-population.
 */

const TITLE_COLOR = '#1A1A2E';
const LABEL_COLOR = '#374151';
const HELPER_COLOR = '#9CA3AF';
const FIELD_BORDER = '#E5E7EB';
const FIELD_TEXT = '#374151';

interface Props {
  responses: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  saving: boolean;
  submittedAt: string | null;
}

function requiredMark(required: boolean) {
  return required ? (
    <Text span c="dangerRed" inherit>
      {' '}
      *
    </Text>
  ) : null;
}

export function PeerLeaderFeedbackFormCard({
  responses,
  onChange,
  onSaveDraft,
  onSubmit,
  saving,
  submittedAt,
}: Props) {
  const isSubmitted = submittedAt !== null;
  // Collapsed by default once submitted (AC12); expandable to review. MED-1077:
  // also collapse on the submit action itself (not just on load).
  const [expanded, setExpanded] = useState<boolean>(!isSubmitted);
  useEffect(() => {
    if (isSubmitted) setExpanded(false);
  }, [isSubmitted]);
  // Surface required-field errors only after a Submit attempt.
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const missingKeys = useMemo(
    () => new Set(getMissingRequiredFeedbackKeys(responses)),
    [responses]
  );

  const handleSubmit = () => {
    if (missingKeys.size > 0) {
      setShowErrors(true);
      return;
    }
    onSubmit();
  };

  const errorFor = (key: string) =>
    showErrors && missingKeys.has(key) ? 'This field is required' : undefined;

  const renderReadOnly = (q: PeerLeaderFeedbackQuestion) => {
    const raw = responses[q.key] ?? '';
    let display = raw;
    if (q.type === 'scale' && raw !== '') {
      display =
        q.scaleOptions?.find(o => String(o.value) === raw)?.label ?? raw;
    } else if (q.type === 'singleSelect' && raw !== '') {
      display = q.selectOptions?.find(o => o.value === raw)?.label ?? raw;
    }
    return (
      <Text size="sm" c={display ? undefined : 'dimmed'}>
        {display || '—'}
      </Text>
    );
  };

  // Single-selects (categorical) and open-text. The 5-point ordinal scales are
  // rendered separately via the shared ScaleQuestion (see the map below).
  const renderInput = (q: PeerLeaderFeedbackQuestion) => {
    const value = responses[q.key] ?? '';
    const error = errorFor(q.key);

    if (q.type === 'singleSelect') {
      return (
        <Radio.Group
          value={value}
          onChange={v => onChange(q.key, v)}
          error={error}
        >
          <Stack gap={6} mt={4}>
            {q.selectOptions?.map(o => (
              <Radio
                key={o.value}
                value={o.value}
                label={o.label}
                color="medleyGreen"
              />
            ))}
          </Stack>
        </Radio.Group>
      );
    }

    return (
      <Textarea
        value={value}
        onChange={e => onChange(q.key, e.currentTarget.value)}
        placeholder={q.placeholder}
        error={error}
        autosize
        minRows={2}
        maxRows={6}
        styles={{
          input: {
            borderColor: FIELD_BORDER,
            borderRadius: 8,
            color: FIELD_TEXT,
            fontSize: 13,
            lineHeight: 1.55,
          },
        }}
      />
    );
  };

  return (
    <ContentCard
      variant="elevated"
      padding="lg"
      fill={false}
      contentGap="md"
      header={
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap={8} align="center" wrap="nowrap">
            <FileText
              size={18}
              color={TITLE_COLOR}
              aria-hidden="true"
              style={{ marginTop: -4 }}
            />
            <Text fz={15} fw={700} c={TITLE_COLOR}>
              Medley Feedback Form
            </Text>
          </Group>
          <Group gap="xs" wrap="nowrap">
            {isSubmitted && (
              <Badge
                color="medleyGreen"
                variant="light"
                leftSection={<CheckCircle size={12} />}
              >
                Submitted
              </Badge>
            )}
            {isSubmitted && (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setExpanded(e => !e)}
                aria-label={
                  expanded ? 'Collapse feedback form' : 'Expand feedback form'
                }
              >
                {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </ActionIcon>
            )}
          </Group>
        </Group>
      }
    >
      {isSubmitted && !expanded && (
        <Text fz={13} c={HELPER_COLOR}>
          Feedback submitted. Expand to review your responses.
        </Text>
      )}

      <Collapse in={expanded}>
        <Stack gap="lg">
          {peerLeaderFeedbackSections.map(section => (
            <Stack key={section.id} gap="sm">
              <div>
                <Text fz={14} fw={700} c={TITLE_COLOR}>
                  {section.title}
                </Text>
                <Text fz={12} c={HELPER_COLOR}>
                  {section.subtitle}
                </Text>
              </div>
              <Divider />
              {section.questions.map(q =>
                q.type === 'scale' && !isSubmitted ? (
                  // 5-pt scales reuse the shared ScaleQuestion so they render
                  // identically to the participant card (MED-1055).
                  <ScaleQuestion
                    key={q.key}
                    label={q.questionText}
                    required={q.required}
                    value={responses[q.key] ? Number(responses[q.key]) : null}
                    options={q.scaleOptions ?? []}
                    onPick={v => onChange(q.key, String(v))}
                    testid={q.key}
                    error={errorFor(q.key)}
                  />
                ) : (
                  <Stack key={q.key} gap={6}>
                    <Text fz={13} fw={600} c={LABEL_COLOR}>
                      {q.questionText}
                      {requiredMark(q.required)}
                    </Text>
                    {isSubmitted ? renderReadOnly(q) : renderInput(q)}
                  </Stack>
                )
              )}
            </Stack>
          ))}

          {!isSubmitted && (
            <Group justify="space-between" align="center">
              <Text fz={12} c={HELPER_COLOR}>
                Draft — not yet submitted
              </Text>
              <Group gap="xs">
                <Button
                  variant="default"
                  size="sm"
                  onClick={onSaveDraft}
                  loading={saving}
                >
                  Save Draft
                </Button>
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  loading={saving}
                  styles={{
                    root: {
                      backgroundColor: POST_SESSION_PRIMARY_BG,
                      color: '#FFFFFF',
                    },
                  }}
                >
                  Submit Feedback
                </Button>
              </Group>
            </Group>
          )}
        </Stack>
      </Collapse>
    </ContentCard>
  );
}

export default PeerLeaderFeedbackFormCard;
